import React from 'react';
import { apiUrl } from '../config.js';

/**
 * User Context for Playground
 * 
 * Manages anonymous user identification for multi-user isolation.
 * Each user gets a unique ID stored in localStorage that persists across sessions.
 * This ensures users only see their own configurations.
 */

const USER_ID_KEY = 'playground_user_id';

const UserContext = React.createContext(null);

export function UserProvider({ children }) {
  const [userId, setUserId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  
  // Initialize user ID from localStorage or generate new one
  React.useEffect(() => {
    let storedUserId = localStorage.getItem(USER_ID_KEY);
    
    if (!storedUserId) {
      // Generate a new user ID
      storedUserId = `user_${generateUserId()}`;
      localStorage.setItem(USER_ID_KEY, storedUserId);
    }
    
    setUserId(storedUserId);
    setIsLoading(false);
  }, []);
  
  // Update user ID from server response (in case server generated one)
  const updateUserId = (newUserId) => {
    if (newUserId && newUserId !== userId) {
      localStorage.setItem(USER_ID_KEY, newUserId);
      setUserId(newUserId);
    }
  };
  
  const value = {
    userId,
    isLoading,
    updateUserId,
  };
  
  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

/**
 * Generate a random user ID
 */
function generateUserId() {
  // Generate a random string for user identification
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hook for making authenticated API calls with user ID
 */
export function useApi() {
  const { userId, updateUserId } = useUser();
  
  const fetchWithUser = React.useCallback(async (url, options = {}) => {
    // Build full URL with API base for production
    const fullUrl = apiUrl(url);
    
    const headers = {
      ...options.headers,
      'X-User-Id': userId || '',
    };
    
    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });
    
    // Update user ID from response if server provides one
    const serverUserId = response.headers.get('X-User-Id');
    if (serverUserId) {
      updateUserId(serverUserId);
    }
    
    return response;
  }, [userId, updateUserId]);
  
  return { fetchWithUser, userId };
}
